import type { AvailableSlot } from './types';

export type SlotDayGroup = {
  dateKey: string;
  label: string;
  slots: AvailableSlot[];
};

function localeTag(language: string): string {
  return language.toLowerCase().startsWith('ua') ? 'uk-UA' : language;
}

export function formatSlotClock(
  iso: string,
  language: string,
  timeZone?: string | null,
): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  if (timeZone) options.timeZone = timeZone;
  return new Intl.DateTimeFormat(localeTag(language), options).format(
    new Date(iso),
  );
}

export function groupAvailableSlots(
  slots: AvailableSlot[],
  language: string,
  timeZone?: string | null,
): SlotDayGroup[] {
  const groups = new Map<string, SlotDayGroup>();
  const dayOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  };
  const keyOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  if (timeZone) {
    dayOptions.timeZone = timeZone;
    keyOptions.timeZone = timeZone;
  }
  const locale = localeTag(language);

  for (const slot of slots) {
    const date = new Date(slot.startsAt);
    const dateKey = new Intl.DateTimeFormat('en-CA', keyOptions).format(date);
    const existing = groups.get(dateKey);
    if (existing) {
      existing.slots.push(slot);
      continue;
    }
    groups.set(dateKey, {
      dateKey,
      label: new Intl.DateTimeFormat(locale, dayOptions).format(date),
      slots: [slot],
    });
  }

  return [...groups.values()].map((group) => ({
    ...group,
    slots: [...group.slots].sort((left, right) =>
      left.startsAt.localeCompare(right.startsAt),
    ),
  }));
}

export function slotRangeIso(days = 14): { from: string; to: string } {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + days);
  return { from: from.toISOString(), to: to.toISOString() };
}
